import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Injector } from '@angular/core'
import { BehaviorSubject, Observable, catchError, forkJoin, of, throwError } from 'rxjs';
import { PasseLivreService } from 'src/app/core/services/passe-livre/passe-livre.service';
import { FlightResponse } from 'src/app/flight/models/FlightResponse';
import { FlightsFormData } from 'src/app/flight/models/FlightsFormData';
import { Direction } from 'src/app/flight/models/Direction';
import { setItemInSessionStorage, FLIGHT_FORM_DATA, CHOOSE_FLIGHT_DATA, removeItemFromSessionStorage } from 'src/app/utils/SessionStorageUtil';
import { formatDate, formatDateToStandard } from 'src/app/utils/DateUtil';
import { assign } from 'lodash';
import { RequestStatus } from 'src/app/models/RequestStatus';
import { MatDialog } from '@angular/material/dialog';
import { DialogInfoComponent } from 'src/app/shared/components/dialog-info/dialog-info.component';
import { AuthorizationService } from 'src/app/core/services/authorization/authorization.service';
import { PasseLivreLatamCrewService } from 'src/app/core/services/passe-livre/passe-livre-latam-crew.service';
import { PasseLivreExternalCrewService } from 'src/app/core/services/passe-livre/passe-livre-external-crew.service';
import { FlightSegmentSch, OTAAirScheduleResponse, OriginDestinationOption } from '../models/OTAAirScheduleResponse';
import { UiService } from 'src/app/shared/services/ui.service';
import { SeatCountResponse, SeatCountFlight } from 'src/app/flight/models/SeatCountResponse';
import { routeSummary, toLegs } from '../flight-ticket-frame/flight-ticket.util';
import { CrewRouteItem } from '../models/CrewRouteItem';

@Component({
    selector: 'app-new-reservation',
    templateUrl: './new-reservation.component.html',
    styleUrls: ['./new-reservation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NewReservationComponent implements OnInit, AfterViewInit {

  Direction = Direction;

  flightsForm: FormGroup = new FormGroup({});
  chooseFlightForm: FormGroup = new FormGroup({});
  
  flightsFormData: FlightsFormData = new FlightsFormData();
  closePanelObservable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  openPanelObservable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  showChooseFlight = false;
  /** The last search answered successfully but without any outbound option. */
  noResults = false;
  proceeded = false;
  flightsRequestStatus: RequestStatus = new RequestStatus();

  outboundFlightsxc: OTAAirScheduleResponse;
  inboundFlightsxc: OTAAirScheduleResponse;

  outboundFlights: OTAAirScheduleResponse[] = [];
  inboundFlights: OTAAirScheduleResponse[] = [];

  topRoutes: CrewRouteItem[] = [];

  private passeLivreService: PasseLivreService;

  constructor(private changeDetector: ChangeDetectorRef, 
              private router: Router,
              private dialog: MatDialog,
              private injector: Injector,
              private authorizationService: AuthorizationService,
              private uiService: UiService) {

    if (this.authorizationService.isExternal()) {
      this.passeLivreService = <PasseLivreExternalCrewService>this.injector.get(PasseLivreExternalCrewService);
    } else {
      this.passeLivreService = <PasseLivreLatamCrewService>this.injector.get(PasseLivreLatamCrewService);
    }

    this.flightsForm.valueChanges.subscribe(() => this.changeDetector.markForCheck());
    this.chooseFlightForm.valueChanges.subscribe(() => this.changeDetector.markForCheck());
    this.openPanelObservable.subscribe(open => { if (open) this.searchOpen = true; });
    this.closePanelObservable.subscribe(close => { if (close) this.searchOpen = false; });
  }

  ngOnInit(): void {
    this.passeLivreService.getTopRoutes().pipe(
      catchError(() => of([] as CrewRouteItem[]))
    ).subscribe(routes => {
      this.topRoutes = routes.slice(0, 6);
      this.changeDetector.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    const dtDeparture = this.flightsForm.get('dtDeparture');
    if (dtDeparture && !dtDeparture.value) {
      dtDeparture.setValue(new Date());
      this.changeDetector.markForCheck();
    }
  }

  selectRoute(route: CrewRouteItem): void {
    this.flightsForm.get('airportOrigin')?.setValue(route.departure);
    this.flightsForm.get('airportDestiny')?.setValue(route.arrival);
  }

  routeKey(route: CrewRouteItem): string {
    return `${route.departure}|${route.arrival}`;
  }

  isRouteSelected(route: CrewRouteItem): boolean {
    return this.flightsForm.get('airportOrigin')?.value === route.departure
      && this.flightsForm.get('airportDestiny')?.value === route.arrival;
  }

  /** Mirrors the search panel state, so the layout can switch between the form and the compact summary. */
  searchOpen = true;

  /** The header toggle of the panel is DOM-driven; keep the state in sync after a click. */
  onSearchPanelToggle(): void {
    const panel = document.querySelector('.reservation-search-panel .expansion-panel');
    this.searchOpen = !!panel?.classList.contains('expansion-panel-opened');
    this.changeDetector.markForCheck();
  }

  searchFlights(): void {
      if (this.flightsForm.valid) {
        this.getFlights();

        setItemInSessionStorage(FLIGHT_FORM_DATA, this.flightsForm.getRawValue());
        removeItemFromSessionStorage(CHOOSE_FLIGHT_DATA);
        
        this.proceeded = false;
        this.chooseFlightForm.reset();
    }    
  }

  getFlights(): void {
    if (this.flightsForm.valid) {

      this.showChooseFlight = true;
      this.noResults = false;
      assign(this.flightsFormData, this.flightsForm.getRawValue());
      this.changeDetector.markForCheck();

      this.flightsRequestStatus.resetValues();
      this.flightsRequestStatus.loading = true;

      // validate showloading
      this.uiService.setLoading(true);
      
      forkJoin(this.createRequestsFlights()).subscribe({
        next: (values: OTAAirScheduleResponse[][]) => {
          this.outboundFlights = this.orderFlights(values[0]);
          this.outboundFlightsxc = this.outboundFlights[0];
          
          if (values[1]) {
            this.inboundFlights = this.orderFlights(values[1]);
            this.inboundFlightsxc = this.inboundFlights[0];
          }
          this.prepareSeatCountsLoading();

          // Desde este punto las tarjetas ya pueden renderizarse.
          this.uiService.setLoading(false);
        
          this.loadSeatCounts();
          this.focusSearchOutcome();

        },
        error: (error) => {
          this.showChooseFlight = false;
          this.flightsRequestStatus.error = true;
          this.openPanelObservable.next(true);

          this.outboundFlights = [];
          this.inboundFlights = [];

          if (error.status === 406) {
            const html: string = `<span class="error-message">${error.error}</span>`;

            this.dialog.open(DialogInfoComponent, {
              width: '600px',
              data: { title: 'Ocorreu um erro!', html }
            });

            this.flightsRequestStatus.setErrorMessage('');
          } else {
            this.flightsRequestStatus.setErrorMessage(error.error);
          }

          this.uiService.setLoading(false);
        }
      }).add(() => {
        this.flightsRequestStatus.loading = false;
        this.changeDetector.markForCheck();
      });
    }


  }

  private prepareSeatCountsLoading(): void {
    const isExternal =
      this.authorizationService.isExternal();
  
    const allOptions = [
      ...(this.outboundFlightsxc
        ?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc
        ?.destinationOptionList ?? [])
    ];
  
    for (const option of allOptions) {
      for (const segment of option.flightSegments) {
        // Fuerza el estado visual "...", incluso si /flights
        // envía availableSeats: null.
        segment.availableSeats = undefined;
  
        // Los externos no pueden seleccionar el vuelo
        // hasta conocer su disponibilidad real.
        if (isExternal) {
          segment.blocked = true;
        }
      }
    }
  }

  /**
   * Recopila segmentos únicos (por flightNumber|origin|fecha) de ida y vuelta,
   * pide el conteo en lote al BL y mapea la respuesta sobre cada FlightSegmentSch.
   *
   * Para LATAM: siempre se muestra totalAvailableSeats (número real o "—" si falla).
   * Para crew externo: idem — el campo `blocked` proviene del GET /flights y no se modifica.
   */
  private loadSeatCounts(): void {
    const allSegments = this.collectAllSegments();
    if (allSegments.length === 0) return;

    this.passeLivreService.getSeatCounts(allSegments).subscribe({
      next: (response: SeatCountResponse) => {
        this.mapSeatCountsToSegments(response.flights);
        this.changeDetector.markForCheck();
      },
      error: () => {
        // Fallo total de red: marcar todos los segmentos como fallidos
        this.markAllSegmentsFailed();
        this.changeDetector.markForCheck();
      }
    });
  }

  /**
   * Devuelve lista de segmentos únicos de ida y vuelta.
   * Extrae la fecha pura (yyyy-MM-dd) del campo departureDateTime (formato "yyyy-MM-dd HH:mm").
   */
  private collectAllSegments(): Array<{ flightNumber: string; origin: string; departureDate: string }> {
    const seen = new Set<string>();
    const result: Array<{ flightNumber: string; origin: string; departureDate: string }> = [];

    const allOptions = [
      ...(this.outboundFlightsxc?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc?.destinationOptionList ?? [])
    ];

    for (const option of allOptions) {
      for (const seg of option.flightSegments) {
        const departureDate = seg.departureDateTime?.substring(0, 10) ?? '';
        const key = `${seg.flightNumber}|${seg.originLocationCode}|${departureDate}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.push({
            flightNumber: seg.flightNumber,
            origin: seg.originLocationCode,
            departureDate
          });
        }
      }
    }
    return result;
  }

  /**
   * Mapea los conteos recibidos sobre cada FlightSegmentSch.
   * Segmentos sin match en la respuesta quedan con availableSeats = null ("—").
   */
  private mapSeatCountsToSegments(flights: SeatCountFlight[]): void {
    const countMap = new Map<string, number | null>();
    for (const f of flights) {
      const key = `${f.flightNumber}|${f.origin}|${f.departureDate}`;
      countMap.set(key, f.totalAvailableSeats);
    }

    const allOptions = [
      ...(this.outboundFlightsxc?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc?.destinationOptionList ?? [])
    ];

    for (const option of allOptions) {
      for (const seg of option.flightSegments) {
        const departureDate = seg.departureDateTime?.substring(0, 10) ?? '';
        const key = `${seg.flightNumber}|${seg.originLocationCode}|${departureDate}`;
        seg.availableSeats = countMap.has(key) ? countMap.get(key)! : null;

        if (this.authorizationService.isExternal()) {
          seg.blocked =
            seg.availableSeats === null ||
            seg.availableSeats <= 0;
        }
      }
    }
  }

  /** Marca todos los segmentos como fallidos (null → la card muestra "—"). */
  private markAllSegmentsFailed(): void {
    const allOptions = [
      ...(this.outboundFlightsxc?.destinationOptionList ?? []),
      ...(this.inboundFlightsxc?.destinationOptionList ?? [])
    ];
    for (const option of allOptions) {
      for (const seg of option.flightSegments) {
        seg.availableSeats = null;
        if (this.authorizationService.isExternal()) {
          seg.blocked = true;
        }
      }
    }
  }

  private createRequestsFlights(): Observable<OTAAirScheduleResponse[]>[] {
    const requests: Observable<OTAAirScheduleResponse[]>[] = [];

    const { airportOrigin, airportDestiny } = this.flightsFormData;
    const dateTimeClient = formatDate(new Date(), 'YYYY-MM-DD HH:mm');
    const dtDeparture = formatDateToStandard(this.flightsFormData.dtDeparture);
    const outbound$ = this.passeLivreService.getFlightsWithConex(dtDeparture, dateTimeClient, airportOrigin, airportDestiny);
    requests.push(outbound$);

    if (this.flightsFormData.isRoundTrip()) {
      const dtArrival = formatDateToStandard(this.flightsFormData.dtArrival);
      const inbound$ = this.passeLivreService.getFlightsWithConex(dtArrival, dateTimeClient, airportDestiny, airportOrigin);
      requests.push(inbound$);
    }
    return requests;
  
  }


  private orderFlights(flights: OTAAirScheduleResponse | OTAAirScheduleResponse[]): OTAAirScheduleResponse[] {
  
    if (Array.isArray(flights)) {
       flights.forEach(otaAirScheduleResponse => {
        this.orderFlightSegments(otaAirScheduleResponse);
      });
  
      return flights;
    } else if (typeof flights === 'object') {
      this.orderFlightSegments(flights);
      return [flights];
    } else {
      console.error('flights no es un array ni un objeto válido');
      return [];
    }
  }
  
  private orderFlightSegments(otaAirScheduleResponse: OTAAirScheduleResponse): void {
    if (!otaAirScheduleResponse.destinationOptionList || !otaAirScheduleResponse.destinationOptionList.length) {
      console.error('destinationOptionList vacío o indefinido');
      return;
    }
  
    otaAirScheduleResponse.destinationOptionList.forEach(option => {
      option.flightSegments.sort((a: FlightSegmentSch, b: FlightSegmentSch) => {
        const aDepartureDateTime = a.departureDateTime;
        const bArrivalDateTime = b.arrivalDateTime;
  
        if (!aDepartureDateTime || !bArrivalDateTime) {
          console.error('Fecha de salida o llegada no definida');
          return 0;
        }
  
        // Convertir tanto la fecha y hora de salida como la de llegada a un formato comparable (milisegundos desde el epoch)
        const aDepartureTime = new Date(aDepartureDateTime).getTime();
        const bArrivalTime = new Date(bArrivalDateTime).getTime();
  
        return aDepartureTime - bArrivalTime;
      });
    });
  }
  
  /**
   * Results first: collapse the search form when every requested leg has options, otherwise
   * keep it open so origin, destination or dates can be corrected right away.
   */
  private focusSearchOutcome(): void {
    const hasOutbound = !!this.outboundFlightsxc?.destinationOptionList?.length;
    const hasInbound = !this.flightsFormData.isRoundTrip() || !!this.inboundFlightsxc?.destinationOptionList?.length;
    this.noResults = !hasOutbound;

    if (hasOutbound && hasInbound) {
      this.closePanelObservable.next(true);
      setTimeout(() => this.revealResults(), 60);
    } else {
      this.openPanelObservable.next(true);
    }
  }

  /** Scrolls to the results summary only when the first option is not already in view. */
  private revealResults(): void {
    const header = document.querySelector<HTMLElement>('app-choose-flight .results-header');
    if (!header) return;

    const navBar = document.getElementById('navBar');
    const topInset = (navBar?.offsetHeight ?? 0) + 12;
    const bottomNav = document.querySelector<HTMLElement>('.main-nav');
    const bottomInset = bottomNav && getComputedStyle(bottomNav).position === 'fixed' ? bottomNav.offsetHeight : 0;
    const visibleBottom = window.innerHeight - bottomInset;

    const first = document.querySelector<HTMLElement>('app-choose-flight .flight-container');
    const firstTop = (first ?? header).getBoundingClientRect().top;
    if (header.getBoundingClientRect().top >= topInset && firstTop + 120 <= visibleBottom) return;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: Math.max(0, header.getBoundingClientRect().top + window.pageYOffset - topInset),
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }

  /** Opens the search form again. The values stay; nothing is requested until the form is submitted. */
  reopenSearch(): void {
    this.openPanelObservable.next(true);
    this.changeDetector.markForCheck();
  }

  private chosen(name: string): OriginDestinationOption | null {
    const value = this.chooseFlightForm.get(name)?.value;
    return value?.flightSegments?.length ? value : null;
  }

  get outboundSelection(): OriginDestinationOption | null {
    return this.chosen(Direction.OUTBOUND.name);
  }

  get inboundSelection(): OriginDestinationOption | null {
    return this.chosen(Direction.INBOUND.name);
  }

  get hasSelection(): boolean {
    return !!this.outboundSelection || !!this.inboundSelection;
  }

  flightNumbers(option: OriginDestinationOption | null): string {
    return (option?.flightSegments ?? []).map(segment => `LA${segment.flightNumber}`).join('/');
  }

  /** "BSB 07:05 → MAO 15:35" of a chosen itinerary, from its first departure and last arrival. */
  routeOf(option: OriginDestinationOption | null): string {
    return routeSummary(toLegs(option?.flightSegments ?? []));
  }

  /** Legs (ida/volta) that still have no selected flight, in display order. */
  get pendingDirections(): Direction[] {
    return [Direction.OUTBOUND, Direction.INBOUND]
      .filter(direction => this.chooseFlightForm.get(direction.name)?.errors?.['required']);
  }

  flightsIsEmpty(): boolean {
    return this.showChooseFlight && this.outboundFlightsxc.destinationOptionList.length == 0;
  }
  
  proceedReservation(): void {
    this.proceeded = true;
    this.changeDetector.detectChanges();
    
    if (this.chooseFlightForm.valid) {
      this.router.navigateByUrl('/confirmation');
    } else {
      setTimeout(() => {
        const targetElement = document.querySelector('.results-header--error')
          || document.querySelector('.results-header-error')
          || document.querySelector('.results-header')
          || document.querySelector('app-choose-flight');

        if (targetElement) {
          const navBar = document.getElementById('navBar');
          const navBarHeight = navBar ? navBar.offsetHeight : 88;
          const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
          const targetScrollTop = Math.max(0, elementTop - navBarHeight - 16);

          window.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
        }
      }, 50);
    }
  }
}
